import { useState, useCallback, useEffect, useRef } from "react";

import {
  CANDY_MACHINE_COMPONENT_ADDRESS,
  DCKSLAP_RESOURCE_ADDRESS,
  RADIX_API_GATEWAY_URL,
  CANDY_MACHINE_BOT_BADGE_ADDRESS,
  GBOF_RESOURCE_ADDRESS,
  CANDY_MACHINE_CANDIES_RESOURCE_ADDRESS,
} from "../../constants";
import { useRadixConnect } from "../../radix_connect";

// Session storage keys
const SESSION_KEYS = {
  PLAYS_COUNT: "candy_shop_plays_count",
  WINS_COUNT: "candy_shop_wins_count",
  BEST_WIN: "candy_shop_best_win",
  SESSION_WINS: "candy_shop_session_wins",
  USER_LEVEL: "candy_shop_user_level",
  DEPOSITED_GBOF: "candy_shop_deposited_gbof",
};

// Game status states
const GAME_STATUS = {
  IDLE: "IDLE",
  PROCESSING_TX: "PROCESSING_TX",
  AWAITING_BOT: "AWAITING_BOT",
  REVEAL: "REVEAL",
  TIMEOUT: "TIMEOUT",
};
const candyMachinRecompense = CANDY_MACHINE_CANDIES_RESOURCE_ADDRESS;

/**
 * Load session stats from localStorage
 */
const loadSessionStats = () => {
  try {
    return {
      playsCount: parseInt(
        localStorage.getItem(SESSION_KEYS.PLAYS_COUNT) || "0",
        10
      ),
      winsCount: parseInt(
        localStorage.getItem(SESSION_KEYS.WINS_COUNT) || "0",
        10
      ),
      bestWin: localStorage.getItem(SESSION_KEYS.BEST_WIN) || null,
      sessionWins: JSON.parse(
        localStorage.getItem(SESSION_KEYS.SESSION_WINS) || "[]"
      ),
    };
  } catch {
    return { playsCount: 0, winsCount: 0, bestWin: null, sessionWins: [] };
  }
};

/**
 * Save session stats to localStorage
 */
const saveSessionStats = (stats) => {
  try {
    localStorage.setItem(SESSION_KEYS.PLAYS_COUNT, stats.playsCount.toString());
    localStorage.setItem(SESSION_KEYS.WINS_COUNT, stats.winsCount.toString());
    if (stats.bestWin) {
      localStorage.setItem(SESSION_KEYS.BEST_WIN, stats.bestWin);
    }
    localStorage.setItem(
      SESSION_KEYS.SESSION_WINS,
      JSON.stringify(stats.sessionWins)
    );
  } catch (e) {
    console.warn("Failed to save session stats:", e);
  }
};

const getLevel = async (accountAddress) => {
  if (!accountAddress) return 0;
  try {
    let cursor = null;
    const allItems = [];

    do {
      const response = await fetch(
        RADIX_API_GATEWAY_URL + "/stream/transactions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            limit_per_page: 50,
            cursor,
            opt_ins: {
              receipt_output: false,
              detailed_events: true,
            },
            affected_global_entities_filter: [
              CANDY_MACHINE_COMPONENT_ADDRESS,
              accountAddress,
            ],
            manifest_accounts_withdrawn_from_filter: [accountAddress],
            manifest_resources_filter: [GBOF_RESOURCE_ADDRESS],
            transaction_status_filter: "Success",
          }),
        }
      );

      const data = await response.json();
      if (data.items) {
        allItems.push(...data.items);
      }
      cursor = data.next_cursor;
    } while (cursor);

    let maxLevel = 0;

    allItems.forEach((tx) => {
      if (
        tx.receipt?.status === "CommittedSuccess" &&
        tx.receipt?.detailed_events
      ) {
        tx.receipt.detailed_events.forEach((event) => {
          if (
            event.identifier?.blueprint === "CandyDispenser" &&
            event.identifier?.event === "UserLevelUpEvent"
          ) {
            const fields = event.payload?.programmatic_json?.fields;
            if (!fields) return;

            const accountField = fields.find((f) => f.field_name === "account");
            const levelField = fields.find((f) => f.field_name === "level");

            if (accountField?.value === accountAddress && levelField) {
              const lvl = parseInt(levelField.value, 10);
              if (!isNaN(lvl) && lvl > maxLevel) {
                maxLevel = lvl;
              }
            }
          }
        });
      }
    });
    return maxLevel;
  } catch (e) {
    console.error("Error fetching level:", e);
    return 0;
  }
};

/**
 * Custom hook for managing the Candy Machine state and interactions
 * Updated for v2 CandyDispenser Scrypto component:
 * - Max level: 11
 * - Choice range: 1-65535 (u16)
 * - Level up condition: deposited_gbof == current_level
 * - Cost to level from N to N+1: N+1 GBOF
 */
export const useCandyMachine = () => {
  const { rdt, selectedAccount } = useRadixConnect();
  // User level state (0-11)
  const [level, setLevel] = useState(() => {
    try {
      const saved = localStorage.getItem(SESSION_KEYS.USER_LEVEL);
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    const fetchLevel = async () => {
      if (selectedAccount?.address) {
        const fetchedLevel = await getLevel(selectedAccount.address);
        setLevel(fetchedLevel);
        localStorage.setItem(SESSION_KEYS.USER_LEVEL, fetchedLevel.toString());
      }
    };
    fetchLevel();
  }, [selectedAccount]);

  // GBOF tokens deposited towards next level
  const [depositedGbof, setDepositedGbof] = useState(() => {
    try {
      const saved = localStorage.getItem(SESSION_KEYS.DEPOSITED_GBOF);
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });

  // Persist level and depositedGbof
  useEffect(() => {
    try {
      localStorage.setItem(SESSION_KEYS.USER_LEVEL, level.toString());
      localStorage.setItem(
        SESSION_KEYS.DEPOSITED_GBOF,
        depositedGbof.toString()
      );
    } catch (e) {
      console.warn("Failed to save user progress:", e);
    }
  }, [level, depositedGbof]);
  // Game status state machine
  const [gameStatus, setGameStatus] = useState(GAME_STATUS.IDLE);
  // Processing state for leveling up
  const [isLevelingUp, setIsLevelingUp] = useState(false);
  // Last reward received
  const [lastReward, setLastReward] = useState(null);
  // Error state
  const [error, setError] = useState(null);

  // Session stats (tracked in localStorage)
  const [sessionStats, setSessionStats] = useState(loadSessionStats);

  // Polling refs
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);

  /**
   * Calculate GBOF needed to reach the next level
   * From Scrypto component logic:
   * - Level up when deposited_gbof == current_level
   * - So to go from level N to N+1, you need to deposit (N+1) GBOF total
   * - But each deposit is counted individually, so you need (level + 1) deposits
   */
  const getGbofNeededForNextLevel = useCallback(() => {
    if (level >= 11) return 0; // Max level reached
    return level + 1;
  }, [level]);

  /**
   * Calculate remaining GBOF needed after current deposits
   */
  const getGbofRemainingForNextLevel = useCallback(() => {
    if (level >= 11) return 0;
    return Math.max(0, level + 1 - depositedGbof);
  }, [level, depositedGbof]);

  // Computed isProcessing for backwards compatibility
  const isProcessing =
    gameStatus !== GAME_STATUS.IDLE && gameStatus !== GAME_STATUS.TIMEOUT;

  /**
   * Get total GBOF required to reach a specific level from 0
   * Sum formula: n*(n+1)/2
   */
  const getTotalGbofForLevel = useCallback((targetLevel) => {
    return (targetLevel * (targetLevel + 1)) / 2;
  }, []);

  /**
   * Update session stats helper
   */
  const updateSessionStats = useCallback((updates) => {
    setSessionStats((prev) => {
      const newStats = { ...prev, ...updates };
      saveSessionStats(newStats);
      return newStats;
    });
  }, []);

  /**
   * Add a session win to the list (for ticker display)
   */
  const addSessionWin = useCallback((win) => {
    setSessionStats((prev) => {
      const newSessionWins = [win, ...prev.sessionWins].slice(0, 20); // Keep last 20
      const newStats = { ...prev, sessionWins: newSessionWins };
      saveSessionStats(newStats);
      return newStats;
    });
  }, []);

  /**
   * Stop polling for balance changes
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  /**
   * Deposit DCKSLAP to play a round (Gacha mechanic)
   * Implements state machine: IDLE -> PROCESSING_TX -> AWAITING_BOT -> REVEAL
   * @param {number} choice - User's choice (1-65535, u16 range)
   */
  const playRound = useCallback(
    async (choice) => {
      if (gameStatus !== GAME_STATUS.IDLE) return;

      const accountAddress = selectedAccount?.address;

      if (!accountAddress) {
        setError("No account connected");
        return;
      }

      // Validate choice is in valid u16 range (1-65535)
      if (choice < 1 || choice > 65535) {
        setError("Choice must be between 1 and 65535");
        return;
      }

      // Step 1: Start processing transaction
      setGameStatus(GAME_STATUS.PROCESSING_TX);
      setError(null);
      const UtcDateNow = new Date().toUTCString();

      console.log('[CandyMachine] 🎰 Initiating play round', {
        account: accountAddress,
        choice: choice,
        timestamp: UtcDateNow
      });

      let txResult = {};
      const manifest = `
          CALL_METHOD
            Address("${accountAddress}")
            "withdraw"
            Address("${DCKSLAP_RESOURCE_ADDRESS}")
            Decimal("1")
          ;
          TAKE_ALL_FROM_WORKTOP
            Address("${DCKSLAP_RESOURCE_ADDRESS}")
            Bucket("dckslap")
          ;
          CALL_METHOD
            Address("${CANDY_MACHINE_COMPONENT_ADDRESS}")
            "deposit_dckslap"
            Bucket("dckslap")
            Address("${accountAddress}")
            ${choice}u16
          ;
          CALL_METHOD
            Address("${accountAddress}")
            "deposit_batch"
            Expression("ENTIRE_WORKTOP")
          ;
      `;

      const result = await rdt.walletApi.sendTransaction({
        transactionManifest: manifest,
        version: 1,
      });

      if (result.isErr()) {
        txResult = {
          success: false,
        };
        console.error('[CandyMachine] ❌ Transaction REJECTED by wallet', {
          error: result.error,
          errorMessage: result.error?.message || 'Unknown error',
          account: accountAddress,
          choice: choice
        });
      } else {
        txResult = {
          success: true,
          transactionId: result.value?.transactionIntentHash || 'pending',
          message: "Transaction submitted - waiting for bot to process",
        };
        console.log('[CandyMachine] ✅ Transaction FUNDED and submitted', {
          transactionId: txResult.transactionId,
          account: accountAddress,
          choice: choice,
          status: 'Awaiting bot processing'
        });
      }

      if (!txResult.success) {
        console.warn('[CandyMachine] ⚠️ Transaction NOT FUNDED - User cancelled or insufficient funds', {
          account: accountAddress,
          choice: choice
        });
        setError(txResult.error || "Transaction failed");
        setGameStatus(GAME_STATUS.IDLE);
        return;
      }

      // Update plays count
      updateSessionStats({ playsCount: sessionStats.playsCount + 1 });

      // Step 2: Transaction confirmed, now wait for bot
      // The bot will detect the DckslapDepositedEvent and call send_candies
      setGameStatus(GAME_STATUS.AWAITING_BOT);
      console.log('[CandyMachine] 🤖 Awaiting bot processing...', {
        transactionId: txResult.transactionId,
        pollingStarted: new Date().toISOString()
      });

      // In production, this will poll for balance changes or listen for events
      // Poll for 60 seconds max
      let retries = 0;
      const MAX_RETRIES = 30;
      let transactionFound = false;

      while (retries < MAX_RETRIES && !transactionFound) {
        try {
          const response = await fetch(
            RADIX_API_GATEWAY_URL + "/stream/transactions",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                limit_per_page: 50,
                from_ledger_state: { timestamp: UtcDateNow },
                opt_ins: {
                  receipt_output: false,
                  balance_changes: true,
                  manifest_instructions: true
                },
                manifest_badges_presented_filter: [
                  CANDY_MACHINE_BOT_BADGE_ADDRESS,
                ],
                transaction_status_filter: "Success",
              }),
            }
          );

          const data = await response.json();

          if (data.items) {
            for (const item of data.items) {
              if (transactionFound) break;

              const manifestInstructions = item.manifest_instructions;

              // Check if this is the correct transaction based on manifest instructions
              // Must contain: send_candies -> accountAddress -> choice
              let isTargetTransaction = false;

              if (typeof manifestInstructions === "string") {
                const sendCandiesIndex = manifestInstructions.lastIndexOf("send_candies");

                if (sendCandiesIndex !== -1) {
                  // Only check the part of the string AFTER "send_candies"
                  const instructionsAfterMethod = manifestInstructions.substring(sendCandiesIndex);

                  // Check if address and choice are present in the remaining string
                  if (
                    instructionsAfterMethod.includes(accountAddress) &&
                    instructionsAfterMethod.includes(`${choice}u16`)
                  ) {
                    isTargetTransaction = true;
                  }
                }
              }

              if (isTargetTransaction) {
                transactionFound = true;
                console.log('[CandyMachine] 🎯 Bot transaction FOUND', {
                  intentHash: item.intent_hash,
                  account: accountAddress,
                  choice: choice,
                  retryAttempt: retries + 1
                });

                const fungibleChanges =
                  item.balance_changes?.fungible_balance_changes;

                if (fungibleChanges && fungibleChanges.length > 0) {
                  const receivedResources = fungibleChanges.filter(
                    (fc) => fc.entity_address === accountAddress
                  );

                  if (receivedResources.length > 0) {
                    const rewards = receivedResources.map((resource) => {
                      const validResource =
                        candyMachinRecompense[resource.resource_address];
                      return {
                        name: validResource ? validResource.name : "Unknown",
                        amount: Number(resource.balance_change),
                        rarity: validResource ? validResource.rarity : "Common",
                        value: 1,
                      };
                    });

                    updateSessionStats({
                      winsCount: sessionStats.winsCount + 1,
                    });

                    rewards.forEach((reward) => {
                      addSessionWin({
                        user: "You",
                        prize: reward.name,
                        amount: reward.amount,
                        timestamp: Date.now(),
                      });
                    });

                    console.log('[CandyMachine] 🎁 REWARDS RECEIVED', {
                      rewards: rewards,
                      totalRewards: rewards.length,
                      account: accountAddress
                    });
                    setLastReward(rewards[0]);
                    setGameStatus(GAME_STATUS.REVEAL);
                    break;
                  }
                }

                // If transaction found but no rewards detected (dry pull)
                console.log('[CandyMachine] 💨 DRY PULL - No rewards detected', {
                  account: accountAddress,
                  choice: choice,
                  intentHash: item.intent_hash
                });
                setLastReward(null);
                setGameStatus(GAME_STATUS.REVEAL);
              }
            }
          }
        } catch (e) {
          console.error("Error polling transactions:", e);
        }

        if (transactionFound) break;

        // Wait a bit before retrying
        retries++;
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      if (!transactionFound) {
        console.error('[CandyMachine] ⏱️ TIMEOUT - Bot transaction not found', {
          account: accountAddress,
          choice: choice,
          maxRetries: MAX_RETRIES,
          pollingDuration: `${MAX_RETRIES * 2}s`
        });
        setGameStatus(GAME_STATUS.TIMEOUT);
      }
    },
    [
      gameStatus,
      sessionStats,
      updateSessionStats,
      addSessionWin,
      selectedAccount,
      rdt,
    ]
  );

  /**
   * Deposit GBOF to level up
   * From Scrypto component logic:
   * - Each deposit is 1 GBOF at a time (component takes 1 from bucket)
   * - If deposited_gbof == level, then user levels up and deposited_gbof resets to 0
   * - Otherwise deposited_gbof += 1
   * - New users start at level 1 immediately on first deposit
   */
  const depositGbof = useCallback(
    async () => {
      if (isLevelingUp) return;
      if (level >= 11) {
        setError("Maximum level reached! (11)");
        return;
      }

      const accountAddress = selectedAccount?.address;

      if (!accountAddress) {
        setError("No account connected");
        return;
      }

      setIsLevelingUp(true);
      setError(null);

      const manifest = `
          CALL_METHOD
            Address("${accountAddress}")
            "withdraw"
            Address("${GBOF_RESOURCE_ADDRESS}")
            Decimal("1")
          ;
          TAKE_ALL_FROM_WORKTOP
            Address("${GBOF_RESOURCE_ADDRESS}")
            Bucket("gbof_bucket")
          ;
          CALL_METHOD
            Address("${CANDY_MACHINE_COMPONENT_ADDRESS}")
            "deposit_gbof"
            Bucket("gbof_bucket")
            Address("${accountAddress}")
          ;
      `;

      let txResult = {};

      const result = await rdt.walletApi.sendTransaction({
        transactionManifest: manifest,
        version: 1,
      });

      if (result.isErr()) {
        txResult = {
          success: false,
          error: result.error.message || "Transaction failed",
        };
        console.error("Transaction error:", result.error, result);
      } else {
        txResult = {
          success: true,
          transactionId: result.value.transactionIntentHash,
          message: `Successfully deposited 1 GBOF`,
        };
      }

      if (!txResult.success) {
        setError(txResult.error || "Transaction failed");
        setIsLevelingUp(false);
        return txResult;
      }

      // Simulate state update
      let currentLevel = level;
      let currentDeposited = depositedGbof;
      let depositsProcessed = 0;

      // Single deposit logic
      if (currentLevel < 11) {
        if (currentDeposited === currentLevel) {
          currentLevel += 1;
          currentDeposited = 0;
        } else {
          currentDeposited += 1;
        }
        depositsProcessed = 1;
      }

      setLevel(currentLevel);
      setDepositedGbof(currentDeposited);
      setIsLevelingUp(false);

      return {
        success: true,
        newLevel: currentLevel,
        depositedGbof: currentDeposited,
        depositsProcessed,
        transactionId: txResult.transactionId
      };
    },
    [isLevelingUp, level, depositedGbof, selectedAccount, rdt]
  );

  /**
   * Reset error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear last reward and reset to idle
   */
  const clearReward = useCallback(() => {
    setLastReward(null);
    setGameStatus(GAME_STATUS.IDLE);
  }, []);

  /**
   * Reset game status (for timeout recovery)
   */
  const resetGameStatus = useCallback(() => {
    stopPolling();
    setGameStatus(GAME_STATUS.IDLE);
    setLastReward(null);
  }, [stopPolling]);

  /**
   * Clear session stats
   */
  const clearSessionStats = useCallback(() => {
    const emptyStats = {
      playsCount: 0,
      winsCount: 0,
      bestWin: null,
      sessionWins: [],
    };
    setSessionStats(emptyStats);
    saveSessionStats(emptyStats);
  }, []);

  return {
    // State
    level,
    depositedGbof,
    isProcessing,
    isLevelingUp,
    lastReward,
    error,
    gameStatus,
    sessionStats,

    // Computed
    gbofNeededForNextLevel: getGbofNeededForNextLevel(),
    gbofRemainingForNextLevel: getGbofRemainingForNextLevel(),
    maxLevel: 11, // Current effective max level

    // Status constants
    GAME_STATUS,

    // Actions
    playRound,
    depositGbof,
    getTotalGbofForLevel,
    getGbofRemainingForNextLevel,
    clearError,
    clearReward,
    resetGameStatus,
    clearSessionStats,
    addSessionWin,
  };
};

export default useCandyMachine;
